import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiCheck, FiZap, FiUsers, FiTrendingUp, FiCreditCard } from 'react-icons/fi';
import api from '../api';
import { useToast } from '../contexts/ToastContext';
import { PORTONE_CONFIG } from '../config/portone';

const PortOne = window.PortOne;

export default function PlanSubscribe() {
  const navigate = useNavigate();
  const toast = useToast();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(false);

  const plans = [
    {
      type: 'basic',
      name: 'Basic',
      price: 7900,
      description: '소규모 학원을 위한 기본 플랜',
      features: [
        '학생 수 제한 없음',
        '매월 10,000P 자동 충전',
        '출결 관리',
        '성적 관리',
        '학부모 알림톡',
        '기본 리포트',
      ],
      icon: FiUsers,
      color: '#4F46E5',
      recommended: false,
    },
    {
      type: 'pro',
      name: 'Pro',
      price: 15900,
      description: '중대형 학원을 위한 프리미엄 플랜',
      features: [
        '학생 수 제한 없음',
        '쿼터 무제한',
        '출결 관리',
        '성적 관리',
        '학부모 알림톡',
        '고급 분석 리포트',
        '우선 고객 지원',
      ],
      icon: FiTrendingUp,
      color: '#7C3AED',
      recommended: true,
    },
  ];

  const handleSubscribe = async () => {
    if (!selectedPlan) {
      toast.warning('플랜을 선택해주세요.');
      return;
    }

    try {
      setLoading(true);

      // 구독 준비
      const prepareRes = await api.post('/api/subscriptions/subscribe', {
        planType: selectedPlan.type,
      });

      const { merchant_uid, amount, name, customer, customerKey, issuesBillingKey } = prepareRes.data;

      // PortOne 빌링키 발급 (결제는 서버에서 진행)
      const response = await PortOne.requestIssueBillingKey({
        storeId: PORTONE_CONFIG.storeId,
        channelKey: PORTONE_CONFIG.channelKey,
        issueId: merchant_uid,  // 이니시스 V2 필수: 빌링키 발급 요청 ID
        issueName: name,  // 이니시스 V2 필수: 빌링키 발급 이름
        billingKeyMethod: 'CARD',
        customer: {
          customerId: customerKey,
          fullName: customer.name,
          email: customer.email,
          phoneNumber: customer.phone,
        },
      });

      if (response.code != null) {
        // 빌링키 발급 실패
        toast.error(`빌링키 발급 실패: ${response.message || '카드 등록에 실패했습니다.'}`);
        setLoading(false);
        return;
      }

      // 응답 구조 확인
      console.log('✅ [PortOne Billing Key Response]:', response);
      console.log('📋 Available fields:', Object.keys(response));

      // 빌링키 발급 완료 - 서버에서 첫 결제 및 구독 생성
      const completeRes = await api.post('/api/subscriptions/complete', {
        billingKey: response.billingKey,
        planType: selectedPlan.type,
        merchant_uid,
        amount,
      });

      if (completeRes.data.success) {
        toast.success('구독이 시작되었습니다!');
        // 구독 성공 페이지로 이동
        navigate(`/subscription/success?planType=${selectedPlan.type}&amount=${amount}`);
      }

    } catch (error) {
      console.error('Subscription error:', error);
      toast.error(error.response?.data?.error || '구독 처리 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>
          <FiZap /> 플랜 구독
        </h1>
        <p>학원에 맞는 플랜을 선택하고 자동 결제를 시작하세요</p>
      </div>

      <div className="subscription-plans">
        {plans.map((plan) => {
          const Icon = plan.icon;
          const isSelected = selectedPlan?.type === plan.type;

          return (
            <div
              key={plan.type}
              className={`plan-card ${isSelected ? 'selected' : ''} ${plan.recommended ? 'recommended' : ''}`}
              onClick={() => setSelectedPlan(plan)}
              style={{ cursor: 'pointer' }}
            >
              {plan.recommended && (
                <div className="recommended-badge">추천</div>
              )}

              <div className="plan-header" style={{ color: plan.color }}>
                <Icon size={40} />
                <h2>{plan.name}</h2>
              </div>

              <div className="plan-price">
                <span className="amount">{plan.price.toLocaleString()}</span>
                <span className="unit">원/월</span>
              </div>

              <p className="plan-description">{plan.description}</p>

              <div className="plan-features">
                {plan.features.map((feature, index) => (
                  <div key={index} className="feature-item">
                    <FiCheck className="feature-icon" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>

              {isSelected && (
                <div className="selected-indicator">
                  <FiCheck size={20} />
                  선택됨
                </div>
              )}
            </div>
          );
        })}
      </div>

      {selectedPlan && (
        <div className="subscription-summary">
          <div className="summary-content">
            <div className="summary-item">
              <span className="label">선택한 플랜</span>
              <span className="value">{selectedPlan.name}</span>
            </div>
            <div className="summary-item">
              <span className="label">월 결제 금액</span>
              <span className="value highlight">{selectedPlan.price.toLocaleString()}원</span>
            </div>
            <div className="summary-item">
              <span className="label">첫 결제</span>
              <span className="value">지금 즉시</span>
            </div>
            <div className="summary-item">
              <span className="label">다음 결제</span>
              <span className="value">30일 후 자동 결제</span>
            </div>
          </div>

          <button
            className="btn-primary btn-block btn-lg"
            onClick={handleSubscribe}
            disabled={loading}
            style={{ marginTop: '24px' }}
          >
            <FiCreditCard />
            {loading ? '처리 중...' : `${selectedPlan.price.toLocaleString()}원 결제하고 구독 시작`}
          </button>

          <p className="subscription-notice">
            * 구독은 매월 자동으로 결제되며, 언제든지 취소할 수 있습니다.<br />
            * 카드 정보는 안전하게 암호화되어 저장됩니다.
          </p>
        </div>
      )}

      <style>{`
        .subscription-plans {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
          gap: 24px;
          margin: 32px 0;
        }

        .plan-card {
          background: white;
          border: 2px solid #E5E7EB;
          border-radius: 12px;
          padding: 32px;
          transition: all 0.3s;
          position: relative;
        }

        .plan-card:hover {
          border-color: #4F46E5;
          transform: translateY(-4px);
          box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
        }

        .plan-card.selected {
          border-color: #4F46E5;
          background: #F5F3FF;
          box-shadow: 0 8px 16px rgba(79, 70, 229, 0.2);
        }

        .plan-card.recommended {
          border-color: #7C3AED;
        }

        .recommended-badge {
          position: absolute;
          top: 16px;
          right: 16px;
          background: linear-gradient(135deg, #7C3AED, #4F46E5);
          color: white;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
        }

        .plan-header {
          text-align: center;
          margin-bottom: 16px;
        }

        .plan-header h2 {
          margin: 8px 0 0 0;
          font-size: 24px;
        }

        .plan-price {
          text-align: center;
          margin: 16px 0;
        }

        .plan-price .amount {
          font-size: 36px;
          font-weight: 700;
          color: #111827;
        }

        .plan-price .unit {
          font-size: 16px;
          color: #6B7280;
          margin-left: 4px;
        }

        .plan-description {
          text-align: center;
          color: #6B7280;
          margin: 16px 0 24px 0;
          font-size: 14px;
        }

        .plan-features {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .feature-item {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 14px;
        }

        .feature-icon {
          color: #10B981;
          flex-shrink: 0;
        }

        .selected-indicator {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          margin-top: 24px;
          padding: 12px;
          background: #4F46E5;
          color: white;
          border-radius: 8px;
          font-weight: 600;
        }

        .subscription-summary {
          background: white;
          border: 2px solid #E5E7EB;
          border-radius: 12px;
          padding: 32px;
          max-width: 600px;
          margin: 32px auto;
        }

        .summary-content {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .summary-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 12px 0;
          border-bottom: 1px solid #F3F4F6;
        }

        .summary-item:last-child {
          border-bottom: none;
        }

        .summary-item .label {
          color: #6B7280;
          font-size: 14px;
        }

        .summary-item .value {
          font-weight: 600;
          color: #111827;
        }

        .summary-item .value.highlight {
          color: #4F46E5;
          font-size: 20px;
        }

        .subscription-notice {
          text-align: center;
          color: #6B7280;
          font-size: 12px;
          margin-top: 16px;
          line-height: 1.6;
        }

        .btn-lg {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          font-size: 16px;
          padding: 16px;
        }
      `}</style>
    </div>
  );
}
